import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Mail, Youtube, Facebook } from "lucide-react";
import { Reveal } from "./Reveal";
import { post } from "@/lib/api";

const OFFICE = [
  { icon: MapPin, label: "Party Office (Registered, per ECI filings)", value: "414, Fourth Floor, Kamat Towers, Patto, Panaji, Goa 403 001" },
  { icon: Mail, label: "Email", value: "contact@revolutionarygoansparty.org" },
];

const SOCIALS = [
  { icon: Youtube, label: "YouTube", href: "https://youtube.com/c/RevolutionaryGoans" },
  { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/profile.php?id=100072353501583" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [attempt, setAttempt] = useState(0);
  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: false });
  };
  const errCls = (k) => (errors[k] ? ` field-error bounce-${attempt % 2}` : "");

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = true;
    if (form.message.trim().length < 5) errs.message = true;
    if (Object.values(errs).some(Boolean)) {
      setErrors(errs);
      setAttempt((a) => a + 1);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      await post("/contact", form);
      toast.success("Message sent! The party office will get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 sec-contact" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <Reveal>
          <p className="eyebrow mb-4">// Reach the Party</p>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Contact & <span className="text-yellow-400">Office Details</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-[1fr_1.2fr] gap-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-4 content-start">
            {OFFICE.map((o, i) => (
              <Reveal key={o.label} delay={i * 0.08}>
                <div className="agenda-card p-6 flex items-start gap-4" data-testid={`office-detail-${i + 1}`}>
                  <div className="bg-yellow-400/10 p-3 shrink-0">
                    <o.icon className="text-yellow-400" size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono-rgp text-[10px] uppercase tracking-[0.25em] text-yellow-400">{o.label}</p>
                    <p className="mt-1 text-sm text-zinc-200 break-all">{o.value}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.24}>
              <div className="agenda-card p-6" data-testid="office-socials-card">
                <p className="font-mono-rgp text-[10px] uppercase tracking-[0.25em] text-yellow-400">Follow the Movement</p>
                <div className="mt-3 flex gap-3">
                  {SOCIALS.map((s) => (
                    <a
                      key={s.label}
                      data-testid={`social-link-${s.label.toLowerCase()}`}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-yellow-400/10 p-3 text-yellow-400 hover:bg-yellow-400 hover:text-black transition-colors"
                      aria-label={s.label}
                    >
                      <s.icon size={20} />
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <form onSubmit={submit} className="agenda-card p-8 lg:p-10 grid sm:grid-cols-2 gap-4" data-testid="contact-form">
              <input data-testid="contact-form-input-name" className={`rgp-input${errCls("name")}`} placeholder="Your Name *" value={form.name} onChange={set("name")} />
              <input data-testid="contact-form-input-email" className={`rgp-input${errCls("email")}`} type="email" placeholder="Your Email *" value={form.email} onChange={set("email")} />
              <input data-testid="contact-form-input-subject" className="rgp-input sm:col-span-2" placeholder="Subject" value={form.subject} onChange={set("subject")} />
              <textarea data-testid="contact-form-input-message" className={`rgp-input sm:col-span-2 min-h-[130px]${errCls("message")}`} placeholder="Your message to the party... *" value={form.message} onChange={set("message")} />
              <button data-testid="contact-form-submit-button" disabled={loading} className="btn-yellow sm:col-span-2 justify-center disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
